import type { FC, ReactNode } from 'react'
import type { PriceTab } from '@/types'

import { ArrowUpRight } from 'lucide-react'
import { formatMoney, formatRange, pad } from '@/lib/format'
import { Eyebrow } from '@/components/generals/eyebrow'
import { TransitionLink } from '@/components/generals/transition-link'
import { Reveal } from '@/components/motion/reveal'
import { fees, priceItems, priceTabs } from '@/constants/pricing'

const tabLinks: Record<PriceTab, { label: string, href: string }> = {
    Springs: { label: "How spring repair works", href: '/services/broken-spring-repair' },
    'Cables & rollers': { label: "How track and cable repair works", href: '/services/off-track-cable-repair' },
    Openers: { label: "See opener options", href: '/services/openers-smart-garage' },
    'New doors': { label: "See every door style", href: '/new-doors' }
}

const groups = priceTabs.map(tab => {
    const items = priceItems.filter(item => item.tab === tab)

    return { tab, items, from: Math.min(...items.map(item => item.lo)) }
})

export const StartingPrices: FC = (): ReactNode => {
    return (
        <div className='relative overflow-hidden rounded-[32px] bg-ink p-6 text-cream shadow-[0_40px_90px_-40px_rgba(21,23,26,0.7)] sm:p-8'>
            <div aria-hidden='true' className='door-grooves pointer-events-none absolute inset-0 opacity-40' />
            <div aria-hidden='true' className='pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-ember/25 blur-[90px]' />

            <div className='relative'>
                <div className='flex items-center justify-between gap-4'>
                    <Eyebrow tone='light'>Where prices begin</Eyebrow>
                    <span className='font-mono text-[10.5px] uppercase tracking-[0.16em] text-cream/45'>2026</span>
                </div>

                <ul className='mt-7'>
                    {groups.map((group, index) => (
                        <li
                            key={group.tab}
                            className='flex items-baseline justify-between gap-4 border-t border-cream/10 py-4 animate-fade-up'
                            style={{ animationDelay: `calc(var(--enter-delay, 0ms) + ${450 + index * 90}ms)` }}
                        >
                            <span className='text-[15.5px] text-cream/80'>{group.tab}</span>

                            <span className='font-mono text-[15px] text-cream'>
                                <span className='text-cream/45'>from </span>
                                {formatMoney(group.from)}
                            </span>
                        </li>
                    ))}
                </ul>

                <p className='mt-2 border-t border-cream/10 pt-5 text-[14px] leading-relaxed text-cream/60'>
                    Prices include parts, labor and haul-away. If we do the repair, you pay no trip fee.
                </p>
            </div>
        </div>
    )
}

export const PriceList: FC = (): ReactNode => {
    return (
        <Reveal selector=':scope > article' className='grid gap-4 md:grid-cols-2'>
            {groups.map((group, index) => (
                <article key={group.tab} className='flex flex-col rounded-[28px] border border-ink/[0.08] bg-card p-6 sm:p-8'>
                    <header className='flex items-start justify-between gap-4'>
                        <div>
                            <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-slate'>
                                {pad(index + 1)} · {group.items.length} {group.items.length === 1 ? "item" : "items"}
                            </p>

                            <h3 className='mt-3 font-heading text-[1.75rem] font-semibold leading-none tracking-[-0.035em] text-ink sm:text-[2rem]'>
                                {group.tab}
                            </h3>
                        </div>

                        <span className='rounded-full bg-sand px-3 py-1.5 font-mono text-[11px] text-ink'>
                            from {formatMoney(group.from)}
                        </span>
                    </header>

                    <ul className='mt-7 flex-1'>
                        {group.items.map(item => (
                            <li key={item.id} className='flex items-end gap-3 border-t border-ink/[0.08] py-4'>
                                <span className='min-w-0'>
                                    <span className='block text-[15.5px] font-medium leading-snug text-ink'>{item.name}</span>
                                    <span className='mt-0.5 block text-[13.5px] text-slate'>{item.note}</span>
                                </span>

                                <span aria-hidden='true' className='mb-1.5 hidden h-px flex-1 border-b border-dotted border-ink/20 sm:block' />

                                <span className='ml-auto shrink-0 font-mono text-[14px] text-ink sm:ml-0'>
                                    {formatRange(item.lo, item.hi)}
                                </span>
                            </li>
                        ))}
                    </ul>

                    <TransitionLink
                        href={tabLinks[group.tab].href}
                        className='group mt-4 inline-flex items-center gap-2 self-start text-[14.5px] font-medium text-ink'
                    >
                        <span className='underline-offset-4 group-hover:underline'>{tabLinks[group.tab].label}</span>
                        <ArrowUpRight className='size-4 text-ember transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' aria-hidden='true' />
                    </TransitionLink>
                </article>
            ))}
        </Reveal>
    )
}

export const FeeGrid: FC = (): ReactNode => {
    return (
        <Reveal
            selector=':scope > li'
            as='ul'
            className='grid gap-px overflow-hidden rounded-[30px] border border-line bg-line sm:grid-cols-2 lg:grid-cols-4'
        >
            {fees.map(fee => (
                <li key={fee.label} className='flex flex-col justify-between gap-10 bg-card p-7 transition-colors duration-500 hover:bg-paper sm:p-8'>
                    <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-slate'>{fee.label}</p>

                    <div>
                        <p className='font-heading text-[2.6rem] font-semibold leading-none tracking-[-0.045em] text-ink'>
                            {fee.value}
                        </p>

                        <p className='mt-3 text-[14.5px] leading-relaxed text-slate'>{fee.note}</p>
                    </div>
                </li>
            ))}
        </Reveal>
    )
}
